import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { JsClientApiService, JsClientConfigDto, ConfigProviderDto, CreateJsClientConfigBody, UpdateJsClientConfigBody } from '../../../services/js-client-api.service';
import { ProvidersApiService, ProviderDto } from '../../../services/providers-api.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { JsEditorComponent } from '../../shared/js-editor/js-editor.component';

export interface ScriptOption {
  label: string;
  value: string;
}

export const AVAILABLE_SCRIPTS: ScriptOption[] = [
  { label: 'Adform',          value: 'adform' },
  { label: 'Audiencerate',    value: 'audiencerate' },
  { label: 'Dynamic Yield',   value: 'dynamicyield' },
  { label: 'Equativ',         value: 'equativ' },
  { label: 'Improve Digital', value: 'improvedigital' },
  { label: 'Liveramp',        value: 'liveramp' },
  { label: 'NeoData',         value: 'neodata' },
  { label: 'Nielsen',         value: 'nielsen' },
  { label: 'Onetag',          value: 'onetag' },
  { label: 'Pubmatic',        value: 'pubmatic' },
  { label: 'Tealium',         value: 'tealium' },
  { label: 'The Trade Desk',  value: 'ttd' },
  { label: 'Weborama',        value: 'weborama' },
  { label: 'Xandr',           value: 'xandr' },
  { label: 'Zeotap',          value: 'zeotap' },
];

const DEFAULT_FONTS_VERSION = 'v2026';

@Component({
  selector: 'app-tracking-scripts-form',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, JsEditorComponent],
  templateUrl: './tracking-scripts-form.component.html',
  styleUrls: ['../tracking-scripts.component.scss']
})
export class TrackingScriptsFormComponent implements OnInit {
  @Input() mode: 'create' | 'edit' = 'create';
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // Form fields
  name = '';
  publisherId = '';
  autostart = true;
  tcf = true;
  publisher = false;
  template: 'tnc' | 'retailor' = 'tnc';
  selectedScriptValues: string[] = [];
  excludeItems: string[] = [];
  fontsVersion = DEFAULT_FONTS_VERSION;
  moduleItems: string[] = [];
  callbackScript = '';
  prepareScript = '';
  overwriteScript = '';
  snippetAppend = '';
  status = 1;
  selectedProviderIds: string[] = [];

  readonly defaultFontsVersion = DEFAULT_FONTS_VERSION;
  readonly availableFontsVersions = ['v2020', 'v2024', 'v2025', 'v2026'];
  readonly availableExcludes = ['fonts', 'audioFP', 'canvas'];
  readonly availableModules = ['equativ', 'pubmatic-convert'];

  saving = false;
  showAdvanced = false;
  providers: ProviderDto[] = [];
  tooltipConfig: { message: string; top: number; left: number } | null = null;
  private tooltipTimer: ReturnType<typeof setTimeout> | null = null;

  readonly availableScripts = AVAILABLE_SCRIPTS;

  private editingId: number | null = null;
  private usedProviderIds: string[] = [];
  private originalDeployedProviders: ConfigProviderDto[] = [];

  constructor(
    private jsClientApi: JsClientApiService,
    private providersApi: ProvidersApiService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Providers are loaded on openCreate/openEdit to always reflect the current entity
  }

  get formTitleKey(): string {
    return this.mode === 'edit'
      ? 'trackingScripts.form.titleEdit'
      : 'trackingScripts.form.titleCreate';
  }

  /**
   * The config owner is never editable after creation: the deployed files live at
   * `{providerID}/config.js` under the owning entity, so moving a config would leave them orphaned.
   */
  get isEntityLocked(): boolean {
    return this.mode === 'edit';
  }

  /**
   * Entities selectable as owner. In global data scope that is every entity the session can see;
   * with a specific entity picked in the sidebar it is that one alone, so the field states the
   * owner instead of leaving it implicit. In edit mode it holds only the owner already stored.
   */
  get entityOptions(): { value: string; label: string }[] {
    if (this.isEntityLocked) {
      return this.publisherId
        ? [{ value: this.publisherId, label: this.authService.getDcrNameById(this.publisherId) ?? this.publisherId }]
        : [];
    }
    const available = this.authService.currentUserValue?.availableDCRs ?? [];
    if (this.authService.hasGlobalDataScope()) {
      return available.map(dcr => ({ value: dcr.id, label: dcr.Name || dcr.name || dcr.id }));
    }
    const activeDcrId = this.authService.getActiveDcrId();
    if (!activeDcrId) {
      return [];
    }
    return [{ value: activeDcrId, label: this.authService.getDcrNameById(activeDcrId) ?? activeDcrId }];
  }

  /**
   * Switching owner invalidates the provider picks: the list is scoped to the entity, so anything
   * already ticked may not belong to the new one.
   */
  onEntityChange(entityId: string): void {
    this.publisherId = entityId;
    this.selectedProviderIds = [];
    this.publisher = false;
    this.providers = [];
    this.loadProviders();
  }

  openCreate(usedProviderIds: string[] = []): void {
    this.usedProviderIds = usedProviderIds;
    this.tooltipConfig = null;
    if (this.tooltipTimer) { clearTimeout(this.tooltipTimer); this.tooltipTimer = null; }
    this.mode = 'create';
    this.editingId = null;
    this.name = '';
    this.publisherId = this.authService.getActiveDcrId() || '';
    this.autostart = true;
    this.tcf = true;
    this.publisher = false;
    this.template = 'tnc';
    this.selectedScriptValues = [];
    this.excludeItems = [];
    this.fontsVersion = DEFAULT_FONTS_VERSION;
    this.moduleItems = [];
    this.callbackScript = '';
    this.prepareScript = '';
    this.overwriteScript = '';
    this.snippetAppend = '';
    this.status = 1;
    this.selectedProviderIds = [];
    this.originalDeployedProviders = [];
    this.loadProviders();
  }

  openEdit(item: JsClientConfigDto, usedProviderIds: string[] = []): void {
    this.usedProviderIds = usedProviderIds;
    this.tooltipConfig = null;
    if (this.tooltipTimer) { clearTimeout(this.tooltipTimer); this.tooltipTimer = null; }
    this.mode = 'edit';
    this.editingId = item.ID;
    this.name = item.Name;
    this.publisherId = item.PublisherID;
    this.autostart = item.Autostart === 1;
    this.tcf = item.TCF === 1;
    this.publisher = item.Publisher === 1;
    this.template = item.Template === 'retailor' ? 'retailor' : 'tnc';
    this.selectedScriptValues = (item.Scripts || []).filter(s =>
      AVAILABLE_SCRIPTS.some(opt => opt.value === s)
    );
    this.excludeItems = item.Exclude || [];
    this.fontsVersion = item.Fonts?.version || DEFAULT_FONTS_VERSION;
    this.moduleItems = item.Modules || [];
    this.callbackScript = item.CallbackScript || '';
    this.prepareScript = item.PrepareScript || '';
    this.overwriteScript = item.OverwriteScript || '';
    this.snippetAppend = item.SnippetAppend || '';
    this.status = item.Status;
    this.selectedProviderIds = (item.Providers || []).map(p => p.ProviderID);
    this.originalDeployedProviders = (item.Providers || []).filter(p => p.DeployStatus === 'deployed');
    this.loadProviders();
  }

  isScriptSelected(value: string): boolean {
    return this.selectedScriptValues.includes(value);
  }

  toggleScript(value: string): void {
    const index = this.selectedScriptValues.indexOf(value);
    if (index === -1) {
      this.selectedScriptValues = [...this.selectedScriptValues, value];
    } else {
      this.selectedScriptValues = this.selectedScriptValues.filter(v => v !== value);
    }
  }

  isProviderUsedElsewhere(providerId: string): boolean {
    return this.usedProviderIds.includes(providerId);
  }

  showDisabledTooltip(id: string, event: MouseEvent): void {
    if (this.tooltipTimer) clearTimeout(this.tooltipTimer);
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const messageKey = id === '__publisher__'
      ? 'trackingScripts.providers.publisherAlreadyInUse'
      : 'trackingScripts.providers.alreadyInUse';
    this.tooltipConfig = {
      message: this.translate.instant(messageKey),
      top: rect.top - 8,
      left: rect.left,
    };
    this.tooltipTimer = setTimeout(() => { this.tooltipConfig = null; }, 2500);
  }

  onPublisherToggle(): void {
    if (!this.publisher) {
      this.selectedProviderIds = this.selectedProviderIds.filter(id => id !== this.publisherId);
      return;
    }
    this.selectedProviderIds = [];
    const existing = this.providers.find(p => p.ID === this.publisherId);
    if (existing) {
      this.selectedProviderIds = [existing.ID];
    } else {
      this.selectedProviderIds = [this.publisherId];
      this.notificationService.notify('info', this.translate.instant('trackingScripts.messages.newProviderWillBeCreated'));
    }
  }

  isExcludeSelected(value: string): boolean {
    return this.excludeItems.includes(value);
  }

  toggleExclude(value: string): void {
    if (this.excludeItems.includes(value)) {
      this.excludeItems = this.excludeItems.filter(v => v !== value);
    } else {
      this.excludeItems = [...this.excludeItems, value];
    }
  }

  isModuleSelected(value: string): boolean {
    return this.moduleItems.includes(value);
  }

  toggleModule(value: string): void {
    if (this.moduleItems.includes(value)) {
      this.moduleItems = this.moduleItems.filter(v => v !== value);
    } else {
      this.moduleItems = [...this.moduleItems, value];
    }
  }

  isProviderSelected(providerId: string): boolean {
    return this.selectedProviderIds.includes(providerId);
  }

  toggleProvider(providerId: string): void {
    const index = this.selectedProviderIds.indexOf(providerId);
    if (index === -1) {
      this.selectedProviderIds = [...this.selectedProviderIds, providerId];
    } else {
      this.selectedProviderIds = this.selectedProviderIds.filter(id => id !== providerId);
    }
    if (providerId === this.publisherId) {
      this.publisher = this.selectedProviderIds.includes(this.publisherId);
    }
  }

  async save(): Promise<void> {
    if (!this.name.trim()) {
      this.notificationService.notify('error', this.translate.instant('trackingScripts.errors.save'));
      return;
    }
    if (!this.publisherId.trim()) {
      this.notificationService.notify('error', this.translate.instant('trackingScripts.errors.save'));
      return;
    }

    if (this.mode === 'edit' && this.editingId) {
      const removedDeployed = this.originalDeployedProviders.filter(
        p => !this.selectedProviderIds.includes(p.ProviderID)
      );
      if (removedDeployed.length > 0) {
        const fileList = removedDeployed.map(p => {
          const provider = this.providers.find(pr => pr.ID === p.ProviderID);
          const name = provider?.Name || p.ProviderID;
          return `  • ${name}  (${p.ProviderID}/config.js)`;
        }).join('\n');
        const message = this.translate.instant(
          'trackingScripts.messages.confirmGcsDeleteWarning',
          { files: fileList }
        );
        if (!window.confirm(message)) {
          return;
        }
      }
    }

    this.saving = true;
    try {
      if (this.mode === 'edit' && this.editingId) {
        const body: UpdateJsClientConfigBody = {
          ID: this.editingId,
          Name: this.name.trim(),
          PublisherID: this.publisherId.trim(),
          Autostart: this.autostart,
          TCF: this.tcf,
          Publisher: this.publisher,
          Template: this.template,
          Scripts: this.selectedScriptValues,
          Exclude: this.excludeItems,
          Fonts: this.fontsVersion !== DEFAULT_FONTS_VERSION ? { version: this.fontsVersion } : null,
          Modules: this.moduleItems.length ? this.moduleItems : [],
          CallbackScript: this.callbackScript.trim() || null,
          PrepareScript: this.prepareScript.trim() || null,
          OverwriteScript: this.overwriteScript.trim() || null,
          SnippetAppend: this.snippetAppend.trim() || null,
          Status: this.status,
          ProviderIDs: this.selectedProviderIds
        };
        await firstValueFrom(this.jsClientApi.updateConfig(body));
        this.notificationService.notify('success', this.translate.instant('trackingScripts.messages.updated'));
      } else {
        const body: CreateJsClientConfigBody = {
          Name: this.name.trim(),
          PublisherID: this.publisherId.trim(),
          Autostart: this.autostart,
          TCF: this.tcf,
          Publisher: this.publisher,
          Template: this.template,
          Scripts: this.selectedScriptValues,
          Exclude: this.excludeItems,
          Fonts: this.fontsVersion !== DEFAULT_FONTS_VERSION ? { version: this.fontsVersion } : null,
          Modules: this.moduleItems.length ? this.moduleItems : [],
          CallbackScript: this.callbackScript.trim() || null,
          PrepareScript: this.prepareScript.trim() || null,
          OverwriteScript: this.overwriteScript.trim() || null,
          SnippetAppend: this.snippetAppend.trim() || null,
          Status: this.status,
          ProviderIDs: this.selectedProviderIds
        };
        await firstValueFrom(this.jsClientApi.createConfig(body));
        this.notificationService.notify('success', this.translate.instant('trackingScripts.messages.created'));
      }
      this.saved.emit();
    } catch (error) {
      console.error('[tracking-scripts-form] save failed', error);
      this.notificationService.notify('error', (error as Error)?.message || this.translate.instant('trackingScripts.errors.save'));
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    if (this.saving) {
      return;
    }
    this.cancelled.emit();
  }

  private loadProviders(): void {
    // Scoped to the owner entity chosen in the form, not to the sidebar selection: in global data
    // scope the sidebar has no entity, and the list would otherwise show every provider there is.
    const ownerEntityId = this.publisherId;
    if (!ownerEntityId) {
      this.providers = [];
      return;
    }
    this.providersApi.getProviders({ status: 'active' }).subscribe({
      next: (list) => {
        // Show global providers (no PublisherID) + providers belonging to the owner entity
        this.providers = list.filter(p => !p.PublisherID || p.PublisherID === ownerEntityId);
      },
      error: (error) => {
        console.error('[tracking-scripts-form] providers load failed', error);
        this.providers = [];
      }
    });
  }
}
